import { useState, useEffect } from 'react';
type RiskLevel = 'low' | 'medium' | 'high' | 'critical';

interface Props {
  score: number;
  level: RiskLevel;
}

const RISK_COLORS: Record<RiskLevel, string> = {
  low: 'var(--risk-low)',
  medium: 'var(--risk-medium)',
  high: 'var(--risk-high)',
  critical: 'var(--risk-critical)',
};

const RISK_LABELS: Record<RiskLevel, string> = {
  low: 'Low Risk',
  medium: 'Medium Risk',
  high: 'High Risk',
  critical: 'Critical Risk',
};

export function RiskScore({ score, level }: Props) {
  const [displayScore, setDisplayScore] = useState(0);
  const color = RISK_COLORS[level];

  // Animate score counting up
  useEffect(() => {
    setDisplayScore(0);
    const duration = 800;
    const steps = 30;
    const increment = score / steps;
    let current = 0;

    const timer = setInterval(() => {
      current += increment;
      if (current >= score) {
        setDisplayScore(score);
        clearInterval(timer);
      } else {
        setDisplayScore(Math.floor(current));
      }
    }, duration / steps);

    return () => clearInterval(timer);
  }, [score]);

  const circumference = 2 * Math.PI * 45;
  const progress = (displayScore / 100) * circumference;

  return (
    <div style={{ textAlign: 'center', padding: '8px 0' }}>
      <div style={{ position: 'relative', display: 'inline-block' }}>
        <svg width="120" height="120" viewBox="0 0 100 100">
          {/* Background circle */}
          <circle
            cx="50"
            cy="50"
            r="45"
            fill="none"
            stroke="var(--bg-tertiary)"
            strokeWidth="8"
          />
          {/* Progress circle */}
          <circle
            cx="50"
            cy="50"
            r="45"
            fill="none"
            stroke={color}
            strokeWidth="8"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={circumference - progress}
            transform="rotate(-90 50 50)"
            style={{ transition: 'stroke-dashoffset 0.3s ease' }}
          />
        </svg>
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          textAlign: 'center',
        }}>
          <div style={{
            fontSize: '32px',
            fontWeight: 700,
            color,
            lineHeight: 1,
          }}>
            {displayScore}
          </div>
          <div style={{
            fontSize: '12px',
            color: 'var(--text-tertiary)',
          }}>
            / 100
          </div>
        </div>
      </div>

      <div style={{
        marginTop: '12px',
        padding: '6px 16px',
        display: 'inline-block',
        background: `${color}20`,
        color,
        fontSize: '14px',
        fontWeight: 600,
        borderRadius: 'var(--radius-full)',
      }}>
        {RISK_LABELS[level]}
      </div>
    </div>
  );
}
